import { useState } from "react";
import { LANDING_URL } from "../../env";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import LanguageToggle from "../../components/Common/LanguageToggle";
import AuthLayout from "../../components/Auth/AuthLayout";
import Button from "../../components/Common/Button";
import ErrorMessage from "../../components/Common/ErrorMessage";
import BackLink from "../../components/Common/BackLink";
import { forgotPassword as forgotPasswordRequest } from "../../api/authApi";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { showFirstFormErrorToast } from "../../utils/formErrorToast";
import { showErrorToast } from "../../utils/toast";

const ForgotPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [submitted, setSubmitted] = useState(false);
    const { run, isLoading, clearError } = useAsyncAction();

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: { email: "" },
    });

    const onSubmit = async (values) => {
        clearError();
        const result = await run(
            () => forgotPasswordRequest({ email: values.email }),
            { errorFallback: t("AUTH_FORGOT_PASSWORD_ERROR_SEND_INSTRUCTIONS") }
        );

        if (result?.success === true || result?.success === 1) {
            setSubmitted(true);
        } else if (result?.success === false || result?.success === 0) {
            showErrorToast(result?.message || t("AUTH_FORGOT_PASSWORD_ERROR_SEND_INSTRUCTIONS"));
        }
    };

    const handleFormInvalid = (formErrors) => {
        showFirstFormErrorToast(formErrors);
    };

    return (
        <>
        <div className="fixed top-6 right-6 z-50">
            <LanguageToggle />
        </div>
        <AuthLayout
            left={(
                <div className="order-2 flex flex-col justify-center px-6 py-10 sm:px-10 lg:order-1 lg:px-16">
                    {!submitted && (
                        <div>
                            <BackLink label={t("COMMON_BACK_TO_SIGN_IN")} to="/login" />
                        </div>
                    )}
                    <div className="mt-10 max-w-md">
                        {submitted ? (
                            <>
                                <div className="flex items-center gap-3 text-sm font-semibold tracking-[0.2em] text-slate-700">
                                    <a href={LANDING_URL} className="hover:opacity-75 transition-opacity">
                                        <img
                                            src="/assets/icons/korres-shares-logo.png"
                                            alt={t("AUTH_KORRES_SHARES")}
                                            className="h-[4.5rem] w-[4.5rem]"
                                        />
                                    </a>
                                </div>
                                <h1 className="mt-8 text-2xl font-semibold text-slate-900">
                                    {t("AUTH_FORGOT_PASSWORD_SUBMISSION_SUCCESS")}
                                </h1>
                                <p className="mt-2 text-sm text-slate-500">
                                    {t("AUTH_FORGOT_PASSWORD_SUBMISSION_MESSAGE")}
                                </p>
                                <div className="mt-8">
                                    <Button
                                        type="button"
                                        className="w-full"
                                        onClick={() => navigate("/login")}
                                    >
                                        {t("COMMON_BACK_TO_SIGN_IN")}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h1 className="text-2xl font-semibold text-slate-900">
                                    {t("AUTH_FORGOT_PASSWORD_TITLE")}
                                </h1>
                                <p className="mt-2 text-sm text-slate-500">
                                    {t("AUTH_FORGOT_PASSWORD_SUBTITLE")}
                                </p>
                                <form className="mt-8 space-y-4" onSubmit={handleSubmit(onSubmit, handleFormInvalid)}>
                                    <div>
                                        <Controller
                                            name="email"
                                            control={control}
                                            rules={{
                                                required: t("AUTH_VALIDATION_EMAIL_REQUIRED"),
                                                pattern: {
                                                    value: /\S+@\S+\.\S+/,
                                                    message: t("AUTH_VALIDATION_EMAIL_INVALID"),
                                                },
                                            }}
                                            render={({ field }) => (
                                                <input
                                                    {...field}
                                                    type="email"
                                                    placeholder={t("AUTH_PLACEHOLDER_EMAIL")}
                                                    className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none transition focus:border-primary"
                                                />
                                            )}
                                        />
                                        <ErrorMessage message={errors.email?.message} />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={!isValid || isLoading}
                                        className="mt-2 w-full"
                                    >
                                        {isLoading ? t("COMMON_SUBMITTING") : t("COMMON_SUBMIT")}
                                    </Button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
            right={(
                <div className="relative order-1 w-full overflow-hidden bg-cream lg:order-2 lg:h-screen">
                    <img
                        src="/assets/images/common/forgot-password.png"
                        alt=""
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
        />
        </>
    );
};

export default ForgotPassword;
